(ns app.front.view.group.admin.approver-groups-domain
  "UI for selecting the active approver (stakeholder) groups for the domain organizational unit. (e.g., Windsor)"
  (:require [app.domain.group.model :as group-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.section :as section]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.ui.table :as ui-table]
            [app.front.util :as futil]
            [app.front.view.group.admin.subs :as gas]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema.core :as s]))

(rf/reg-sub ::domain-group-name
  :<- [::gas/group]
  :-> :group/name)

(rf/reg-sub ::groups-in-use
  :-> ::groups-in-use)

(rf/reg-event-fx ::fetch-approver-groups-domain
  (fn [_ [_ group-id]]
    {:comm/command {:command    :group/approver-groups-domain
                    :method     :get
                    :data       {:domain-group-id group-id}
                    :on-success [::fetch-approver-groups-domain.success]}}))

(rf/reg-event-fx ::fetch-approver-groups-domain.success
  (fn [{:keys [db]} [_ status {:keys [entities groups-in-use]}]]
    {:db (assoc db ::groups-in-use groups-in-use)
     :fx [[:dispatch [::entities/merge-entity-db status entities]]]}))

(rf/reg-sub ::group-is-local-gov-domain?
  :<- [::gas/group]
  :-> group-model/local-gov-domain?)

(rf/reg-sub ::approver-groups
  :<- [::gas/group-id]
  :<- [::entities/entities (filter #(some-> % :group/roles :group.role/approver))]
  (fn [[domain-group-entity-id approver-user-groups] _]
    ;; make sure that (approver) groups belong to correct domain group
    (->> approver-user-groups
         (map :entity/parent)
         (filter #(= (-> % :entity/parent :db/id) domain-group-entity-id)))))

(rf/reg-event-db ::group-open
  (fn [db [_ group]]
    (assoc db ::edited-group (if group
                               (select-keys group [:db/id :group/name])
                               {:group/name ""}))))

(rf/reg-event-db ::edited-group-close
  (fn [db _]
    (dissoc db ::edited-group)))

(rf/reg-event-db ::edited-group-set-name
  (fn [db [_ name]]
    (assoc-in db [::edited-group :group/name] name)))

(rf/reg-sub ::edited-group
  :-> ::edited-group)

(rf/reg-sub ::edited-group-name
  :<- [::edited-group]
  :-> :group/name)

(rf/reg-sub ::edited-group-name-taken?
  :<- [::approver-groups]
  :<- [::edited-group]
  (fn [[groups edited]]
    (when edited
      (-> (remove #(= (:db/id %) (:db/id edited)) groups)
          (p/find-first #(str-util/normalized-equals? (:group/name %)
                                                      (:group/name edited)))
          some?))))

(rf/reg-sub ::edited-group-save-disabled?
  :<- [::edited-group-name]
  :<- [::edited-group-name-taken?]
  (fn [[group-name name-taken?]]
    (or (str/blank? group-name)
        name-taken?)))

(rf/reg-event-fx ::save-approver-group
  (fn [{:keys [db]} _]
    (let [{:group/keys [name] :as group} (::edited-group db)
          group-id                       (::gas/group-id db)]
      {:comm/command (if (:db/id group)
                       {:command          :group/edit-approver-group
                        :data             {:group-id   (:db/id group)
                                           :group-name name}
                        :on-success       [::entities/apply-changes]
                        :notify           {:success :changes-saved}
                        :finally-dispatch [::edited-group-close]}
                       {:command          :group/create-approver-group
                        :data             {:group-name      name
                                           :domain-group-id group-id}
                        :on-success       [::entities/apply-changes]
                        :notify           {:success :approver-group/created}
                        :finally-dispatch [::edited-group-close]})})))

(rf/reg-event-fx ::remove-approver-group
  (fn [_ [_ group-id]]
    {:comm/command {:command    :group/remove-approver-group
                    :data       (util/mmap group-id)
                    :on-success [::entities/apply-changes]
                    :notify     {:success :approver-group/removed}}}))

(defn group-modal [tr]
  (let [edited-group (<sub [::edited-group])
        new?         (-> edited-group :db/id nil?)
        name         (<sub [::edited-group-name])
        disabled?    (<sub [::edited-group-save-disabled?])
        name-taken?  (<sub [::edited-group-name-taken?])
        close-fn     #(>evt [::edited-group-close])]
    [ui-modal/composed-input-modal
     {:open                   (some? edited-group)
      :selector-primary-focus "#approver-group-name"
      :on-close               close-fn}
     (cc/modal-header {:close-modal close-fn
                       :title       (tr [(if new? :approver-group/add :approver-group/edit)])})
     (when edited-group
       (cc/modal-body
         {}
         [:div.my-4
          [ui/text-input {:id           "approver-group-name"
                          :label-text   (tr [:admin/group-name])
                          :value        name
                          :invalid      name-taken?
                          :invalid-text (when name-taken? (tr [:admin/group-name-taken]))
                          :on-change    #(>evt [::edited-group-set-name (futil/tval %)])}]]))
     (cc/modal-footer
       {:primary-button-text     (tr [(if new? :create :save)])
        :primary-button-disabled disabled?
        :secondary-button-text   (tr [:cancel])
        :on-request-submit       #(>evt [::save-approver-group])
        :on-request-close        close-fn})]))

(defn approver-groups-form [tr]
  (let [groups                     (<sub [::approver-groups])
        show-user-management-link? (ff/enabled? :feature-flag.id/authority-user-manages-users-in-approvers-groups)
        user-management-route      (<sub [::gas/user-management-route])]
    [:<>
     [:div.px-8.flex.justify-between
      [:h1 (tr [:approvals/groups] [(<sub [::domain-group-name])])]]
     [:section>fieldset
      (if (empty? groups)
        (tr [:approvals/no-groups])
        (r/with-let [sorting* (r/atom [::_ false])]
          #_{:clj-kondo/ignore [:deprecated-var]}
          [ui-table/table
           {:tr tr}
           [[ui-table/table-header-sortable tr sorting* :approvals/name]]
           (for [{gid   :db/id
                  gname :group/name} (cond->> (sort-by (first @sorting*) groups)
                                       (second @sorting*) reverse)
                 :let                [group-name-or-link (if show-user-management-link?
                                                           (cc/link {:href (href/href user-management-route {:group-id gid})} gname)
                                                           gname)]]
             {:key  gid
              :cols [{:content group-name-or-link}]})]))]]))

(defn domain-approver-groups [tr]
  (let [groups                (<sub [::approver-groups])
        groups-in-use         (<sub [::groups-in-use])
        user-management-route (<sub [::gas/user-management-route])]
    [:<>
     [group-modal tr]
     [section/section
      "approver-groups"
      (util/mmap {:heading-button   [ui/button {:id       "add-approver-group-button"
                                                :icon     i/plus
                                                :on-click #(>evt [::group-open])}
                                     (tr [:admin/add-group])]
                  :always-expanded? true}
                 tr)
      (tr [:approvals/groups] [(<sub [::domain-group-name])])
      [sortable-table/table {:cols            [[:approvals/name
                                                :group/name
                                                {:sorting true}]]
                             :items           groups
                             :link            {:description (tr [:group-admin/user-management])
                                               :make-href   #(href/href user-management-route {:group-id (:db/id %)})}
                             :initial-sorting [:group/name :asc]
                             :make-actions    (fn [{:db/keys [id] :as group}]
                                                [(when (not (contains? groups-in-use id))
                                                   {:title           (tr [:delete])
                                                    :icon            i/trash
                                                    :confirm?        true
                                                    :confirm-content (tr [:approver-group/remove-confirm]
                                                                         [(:group/name group)])
                                                    :on-click        #(>evt [::remove-approver-group id])})
                                                 {:title    (tr [:edit])
                                                  :icon     i/pen
                                                  :on-click #(>evt [::group-open group])}])
                             :tr              tr}]]]))

(defn view [match]
  (let [tr (<sub [::gas/group-tr])]
    (when (<sub [::group-is-local-gov-domain?])
      [:div.cloudpermit-page
       [ui/page-header (util/mmap match)
        (if (ff/enabled? :feature-flag.id/approver-group-creation-for-domains)
          [domain-approver-groups tr]
          [approver-groups-form tr])]])))

(def route
  {:name        :view/group.admin.approver-groups-domain
   :view        view
   :public?     false
   :parameters  {:path {:group-id s/Int}}
   :controllers [{:parameters {:path [:group-id]}
                  :start      (fn [{{group-id :group-id} :path}]
                                (>evt [::fetch-approver-groups-domain group-id]))}]})
